interface QueuedPromise<T = any> {
  promise: () => Promise<T>;
  resolve: (value: T) => void;
  reject: (reason?: any) => void;
}

export class PromiseQueue {
  /**
   * Promises in a row.
   */
  private queue: QueuedPromise[] = [];

  /**
   * Indicator that a Promise is currently being processed.
   */
  private working: boolean = false;

  /**
   * Enqueue a Promise.
   * This adds the Promise to the queue. If the queue is empty, the Promise is started immediately.
   * @param promise Function that returns the Promise.
   * @returns A promise that is redeemed (or rejected) as soon as the queued promise has been processed.
   */
  public enqueue<T = void>(promise: () => Promise<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      this.queue.push({
        promise,
        resolve,
        reject,
      });
      this.dequeue();
    });
  }

  /**
   * Get the first Promise from the queue and start it, unless a Promise is already active.
   * @returns `true` if a Promise has been started from the queue or `false` if a Promise is already active or the queue is empty.
   */
  private dequeue(): boolean {
    if (this.working) {
      return false;
    }

    const item = this.queue.shift();
    if (!item) {
      return false;
    }

    try {
      this.working = true;
      item
        .promise()
        .then(value => {
          item.resolve(value);
        })
        .catch(err => {
          item.reject(err);
        })
        .finally(() => {
          this.working = false;
          this.dequeue();
        });
    } catch (err) {
      item.reject(err);
      this.working = false;
      this.dequeue();
    }

    return true;
  }
}
